import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { apiRequest } from "../../../api/http";
import { PayrollListResponse, PayrollListItem } from "../../../types/admin";
import { isPayrollFileOnly } from "../../../utils/payroll";
import { getWeekRangeLabels } from "../../../utils/workCalendar";

interface PayrollTabProps {
  branchId: number;
}

const PayrollTab: React.FC<PayrollTabProps> = ({ branchId }) => {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [payrolls, setPayrolls] = useState<PayrollListItem[]>([]);
  const [weekStartWeekday, setWeekStartWeekday] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);

  const fetchPayrolls = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest<PayrollListResponse>(
        `/admin/branches/${branchId}/payrolls?year=${year}&month=${month}`
      );
      setPayrolls(res.items);
      setWeekStartWeekday(res.week_start_weekday || 1);
    } catch (err: any) {
      setError(err.message || "급여대장 데이터를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPayroll = (item: PayrollListItem) => {
    navigate(`/admin/branches/${branchId}/payrolls/${item.payroll_id}`);
  };

  const weekHeaders = getWeekRangeLabels(year, month, weekStartWeekday);

  const renderAllowance = (amount: number | undefined | null) => {
    if (!amount) return "0";
    return `${amount.toLocaleString()}`;
  };

  const handleExcelDownload = () => {
    const wsData = [
      ["점명", "근무자", "총근무시간", "근무일수", "시급", "주휴수당", "", "", "", "", "", "급여"],
      ["", "", "", "", "", "주근로시간", weekHeaders[0], weekHeaders[1], weekHeaders[2], weekHeaders[3], weekHeaders[4], ""]
    ];
    
    let totalWorkMinutes = 0;
    let totalNetPay = 0;

    payrolls.forEach((item, index) => {
      totalWorkMinutes += item.total_work_minutes;
      totalNetPay += item.net_pay;

      const branchName = item.branch_name || "-";
      wsData.push([
        index === 0 ? branchName : "", // 점명 (first row only in excel for merging, or just print it)
        item.employee_name,
        (Math.floor(item.total_work_minutes / 60) + (item.total_work_minutes % 60) / 60).toFixed(1),
        item.total_work_days?.toString() || "0",
        item.hourly_wage?.toLocaleString() || "-",
        item.weekly_work_hours?.toString() || "-",
        renderAllowance(item.week1_allowance), 
        renderAllowance(item.week2_allowance), 
        renderAllowance(item.week3_allowance), 
        renderAllowance(item.week4_allowance), 
        renderAllowance(item.week5_allowance),
        item.net_pay.toLocaleString()
      ]);
    });

    const firstBranchName = payrolls.length > 0 ? (payrolls[0].branch_name || "합계") : "합계";
    wsData.push([
      `${firstBranchName} 계`,
      payrolls.length.toString(),
      (Math.floor(totalWorkMinutes / 60) + (totalWorkMinutes % 60) / 60).toFixed(1),
      "", "", "", "", "", "", "", "",
      totalNetPay.toLocaleString()
    ]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Merge headers for 주휴수당 and 점명 if needed
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push(
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // 점명
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // 근무자
      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // 총근무시간
      { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // 근무일수
      { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } }, // 시급
      { s: { r: 0, c: 5 }, e: { r: 0, c: 10 } }, // 주휴수당 (col 5 to 10)
      { s: { r: 0, c: 11 }, e: { r: 1, c: 11 } } // 급여
    );

    if (payrolls.length > 1) {
      ws['!merges'].push({ s: { r: 2, c: 0 }, e: { r: 2 + payrolls.length - 1, c: 0 } }); // Merge 점명 column in data
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "급여대장");
    XLSX.writeFile(wb, `${year}년_${month}월_급여대장.xlsx`);
  };

  useEffect(() => {
    fetchPayrolls();
  }, [branchId, year, month]);

  return (
    <div className="payroll-tab">
      <div className="filter-bar" style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#666874' }}>년월</label>
          <input 
            type="month" 
            value={`${year}-${month.toString().padStart(2, '0')}`} 
            onChange={(e) => {
              const [y, m] = e.target.value.split('-');
              if (y && m) {
                setYear(parseInt(y));
                setMonth(parseInt(m));
              }
            }}
            style={{ padding: '8px', border: '1px solid #e7e8ef', borderRadius: '8px' }}
          />
        </div>
      </div>

      {loading && <p>데이터를 불러오는 중입니다...</p>}
      {error && <p className="error-text">{error}</p>}

      {!loading && !error && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button 
              onClick={handleExcelDownload}
              style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #d9d9d9', cursor: 'pointer' }}
            >
              엑셀 다운로드
            </button>
          </div>
          <div className="table-shell mt-4" style={{ overflowX: 'auto' }}>
            <table className="admin-table compact" style={{ whiteSpace: 'nowrap', borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
              <thead style={{ backgroundColor: '#f5f5f5' }}>
                <tr>
                  <th rowSpan={2} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>점명</th>
                  <th rowSpan={2} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>근무자</th>
                  <th rowSpan={2} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>총근무시간</th>
                  <th rowSpan={2} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>근무일수</th>
                  <th rowSpan={2} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>시급</th>
                  <th colSpan={6} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>주휴수당</th>
                  <th rowSpan={2} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>급여</th>
                  <th rowSpan={2} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', minWidth: '80px' }}>상세</th>
                </tr>
                <tr>
                  <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>주근로시간</th>
                  <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{weekHeaders[0]}</th>
                  <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{weekHeaders[1]}</th>
                  <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{weekHeaders[2]}</th>
                  <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{weekHeaders[3]}</th>
                  <th style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{weekHeaders[4]}</th>
                </tr>
              </thead>
              <tbody>
                {payrolls.map((item, index) => (
                  <tr key={item.payroll_id}>
                    {index === 0 && (
                      <td rowSpan={payrolls.length > 0 ? payrolls.length : 1} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: '#fdfdfd' }}>
                        {item.branch_name || "-"}
                      </td>
                    )}
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.employee_name}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{(Math.floor(item.total_work_minutes / 60) + (item.total_work_minutes % 60) / 60).toFixed(1)}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.total_work_days?.toString() || "0"}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.hourly_wage?.toLocaleString() || "-"}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{item.weekly_work_hours?.toString() || "-"}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{renderAllowance(item.week1_allowance)}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{renderAllowance(item.week2_allowance)}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{renderAllowance(item.week3_allowance)}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{renderAllowance(item.week4_allowance)}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{renderAllowance(item.week5_allowance)}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center', backgroundColor: '#fdfbc8' }}>{item.net_pay.toLocaleString()}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                      <button
                        className="detail-icon-btn"
                        type="button"
                        style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                        onClick={() => handleOpenPayroll(item)}
                      >
                        {isPayrollFileOnly(item) ? "파일보기" : "상세"}
                      </button>
                    </td>
                  </tr>
                ))}
                {payrolls.length > 0 && (
                  <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <td colSpan={1} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{payrolls[0]?.branch_name || "합계"} 계</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>{payrolls.length}</td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                      {(Math.floor(payrolls.reduce((sum, item) => sum + item.total_work_minutes, 0) / 60) + (payrolls.reduce((sum, item) => sum + item.total_work_minutes, 0) % 60) / 60).toFixed(1)}
                    </td>
                    <td colSpan={8} style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}></td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}>
                      {payrolls.reduce((sum, item) => sum + item.net_pay, 0).toLocaleString()}
                    </td>
                    <td style={{ border: '1px solid #e0e0e0', padding: '8px', textAlign: 'center' }}></td>
                  </tr>
                )}
                {payrolls.length === 0 && (
                  <tr>
                    <td colSpan={13} className="empty-row" style={{ border: '1px solid #e0e0e0', padding: '16px', textAlign: 'center' }}>데이터가 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PayrollTab;
